import { useEffect, useRef, useState } from "react";
import * as scheduleApi from "../../api/schedule";
import { InstallationSchedule } from "../../types";
import { coloreStato } from "../../utils/statoColors";
import { Input } from "./Input";

interface ScheduleAutocompleteProps {
  label: string;
  /** Testo mostrato nel campo (sintesi insegna/località della scheda scelta). */
  displayValue: string;
  onSelect: (scheda: InstallationSchedule) => void;
  onClear: () => void;
}

/**
 * Cerca tra le schede del Calendario (installation_schedule) mentre si scrive.
 * È la FONTE REALE delle installazioni: il Modulo `installations` è vuoto in
 * produzione, quindi l'autocomplete delle Attivazioni cerca qui.
 */
export function ScheduleAutocomplete({ label, displayValue, onSelect, onClear }: ScheduleAutocompleteProps) {
  const [query, setQuery] = useState(displayValue);
  const [suggestions, setSuggestions] = useState<InstallationSchedule[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => setQuery(displayValue), [displayValue]);

  useEffect(() => {
    if (query.trim().length < 2 || query === displayValue) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { records } = await scheduleApi.listSchedule({ search: query });
        setSuggestions(records);
        setOpen(records.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(scheda: InstallationSchedule) {
    setQuery(`${scheda.insegna ?? "—"} · ${scheda.localita ?? "—"}`);
    onSelect(scheda);
    setOpen(false);
  }

  function handleChange(value: string) {
    setQuery(value);
    if (value === "") onClear();
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        value={query}
        placeholder="Cerca per insegna o località…"
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-black/10 bg-white shadow-lg">
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleSelect(s)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-primary/5"
            >
              <span className="flex-1 truncate">
                <span className="font-medium text-ink">{s.insegna ?? "—"}</span>
                <span className="text-ink/50">
                  {" "}
                  — {s.localita ?? "—"}
                  {s.tipo_dispositivo ? ` · ${s.tipo_dispositivo}` : ""}
                </span>
              </span>
              {s.stato && (
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white"
                  style={{ backgroundColor: coloreStato(s.stato) ?? "#6b7280" }}
                >
                  {s.stato}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
